/**
 * `tier_block_render` emitter for the Manager+ pipeline gate (SE-5508).
 *
 * Backs the epic's zero-leak metric: it counts the times a below-Manager
 * ("Scout tier", `RoleName.Member`) user was denied a pipeline surface, and it
 * must NEVER count a Manager+ user. The metric is a binary gate this release —
 * a surface is either withheld in full or granted in full; there is no
 * partial/gated-element case to report.
 *
 * ## Why this is keyed off the ROLE, not `useIsPipelineSurfaceActive`
 *
 * `useIsPipelineSurfaceActive()` is `PIPELINE_SURFACE === "on" && isManagerPlus`,
 * and the flag ships `"off"`. Emitting on that predicate would fire for EVERY
 * user — Managers and Owners included — which is precisely what AC3 forbids.
 * The thing being instrumented is the TIER denial, so the condition is the tier
 * check alone (`useIsPipelineManager`'s role set). That also makes the event
 * independent of the build-time flag, so it produces real data today rather
 * than waiting on SE-6257.
 *
 * ## Fail-closed is not fail-emit
 *
 * `useIsPipelineManager` deliberately returns `false` while `/auth/me` is in
 * flight, so gating the emission on that boolean would emit a spurious block
 * for a Manager during every cold load — breaking "NEVER fires for Manager+".
 * This hook therefore waits for the role to actually RESOLVE and emits only
 * for a resolved, non-Manager+ role. An unauthenticated or still-loading
 * session emits nothing.
 *
 * ## Exactly-once
 *
 * The guard is MODULE-level, not per-hook-instance, because more than one
 * component can mount the same gate at the same time: `useNavItems` is
 * consumed by both the tabs layout and `LeftSidebar`, so an instance-scoped
 * ref would emit the nav block twice on a wide viewport. One emission per
 * (surface, role) per session is the contract. Re-renders, remounts, and
 * additional consumers of the same surface are all absorbed.
 * @module features/player-kanban/hooks/usePipelineTierBlock
 */

import { useEffect } from "react";

import { AnalyticsTracker } from "@/features/analytics";
import type { TTierBlockSurface } from "@/features/analytics";
// The direct provider path, not the `@/features/auth` barrel: the barrel
// re-exports auth SCREENS, which pull in `expo-device` and drag a whole
// native-module graph into every consumer of this hook (it broke
// `useNavItems`'s suite outright). Same import `useIsContractOwner` uses.
import { useCurrentUser } from "@/features/auth/providers/CurrentUser";

import { MANAGER_PLUS_ROLES } from "../constants/pipelineTierRoles";

/**
 * Surfaces already reported this session, as `"<surface>:<role>"` keys.
 *
 * A holder object reassigned wholesale (never mutated in place), mirroring
 * `organizationContextHolder` in `features/analytics/utils/posthogSender`.
 * Keying by role as well as surface means a session that switches to a
 * different user reports that user's denial too.
 */
const reportedHolder: { current: ReadonlySet<string> } = {
  current: new Set<string>(),
};

/**
 * Clears the once-per-session emission guard.
 *
 * Exported for tests, which must be able to assert the first emission and the
 * suppressed second one independently across cases.
 */
export const resetPipelineTierBlockGuard = (): void => {
  reportedHolder.current = new Set<string>();
};

/**
 * Report that a pipeline surface was withheld from a below-Manager user.
 * @param surface - Which pipeline surface withheld access.
 */
export function usePipelineTierBlock(surface: TTierBlockSurface): void {
  const currentUser = useCurrentUser();
  const roleName = currentUser?.role?.name;

  useEffect(() => {
    // Identity has not resolved (loading, or signed out). Not a denial — say
    // nothing rather than attribute a block to an unknown tier.
    if (!roleName) return;

    // AC3's second clause. Owner/Admin/Manager are never blocked, so they must
    // never appear in this event's volume at all.
    if (MANAGER_PLUS_ROLES.has(roleName)) return;

    const key = `${surface}:${roleName}`;
    if (reportedHolder.current.has(key)) return;
    reportedHolder.current = new Set([...reportedHolder.current, key]);

    void AnalyticsTracker.pipeline.logTierBlockRender({
      surface,
      role: roleName,
    });
  }, [roleName, surface]);
}
